import { useMemo } from 'react';
import { CheckCircleIcon } from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Field } from '@/modules/primitives3D/editorShell';
import { FieldLabel, FlowTitle } from '@/components/ui/text';
import { useSceneHealth } from '@/hooks/useSceneHealth';
import { deviceBlockStateFromVerdicts } from '@/modules/sceneHealth';
import { DEVICE_LABELS } from '../deviceAvailability';
import { SharePanel } from '../SharePanel';
import type { AnchorCategory, PublishDevice } from '../types';
import { shownUrl } from '@/utils/localCopies';

interface Props {
  devices: PublishDevice[];
  storyName: string;
  submitted: boolean;
  sceneInstanceId?: string | null;
  coverImageUrl?: string | null;
  anchorCategory?: AnchorCategory;
}

const DEVICE_ORDER: PublishDevice[] = ['app', 'appless', 'spectacles'];

export function ConfirmationStep({ devices, storyName, submitted, sceneInstanceId, coverImageUrl, anchorCategory }: Props) {
  const shareDevices = useMemo(() => DEVICE_ORDER.filter((d) => devices.includes(d)), [devices]);

  const { data: sceneHealth } = useSceneHealth(submitted ? sceneInstanceId : null);
  const partialBlocks = useMemo(
    () => Object.fromEntries(deviceBlockStateFromVerdicts(sceneHealth?.verdicts).partialBlocks) as Partial<Record<PublishDevice, string>>,
    [sceneHealth],
  );

  if (!submitted) {
    return (
      <div className="relative h-full flex items-center">
        <span aria-hidden className="pointer-events-none absolute top-0 bottom-0 left-1/2 w-px bg-lureo-neutral-strong" />
        <div className="w-full grid grid-cols-2 gap-5 items-stretch">
          <Card>
            <div className="space-y-3">
              <Field>
                <FieldLabel mb>Story</FieldLabel>
                <p className="font-lureo-font-ui font-medium text-lureo-14 text-lureo-ink leading-[1.3]">
                  {storyName || '—'}
                </p>
              </Field>
              <Field>
                <FieldLabel mb>Platforms</FieldLabel>
                <div className="flex flex-wrap gap-2">
                  {shareDevices.length === 0 ? (
                    <span className="font-lureo-font-ui text-lureo-14 text-lureo-ink-subtlest">—</span>
                  ) : (
                    shareDevices.map((d) => (
                      <span
                        key={d}
                        className="inline-flex items-center rounded-lureo-radius-pill bg-lureo-brand-ink/10 border-lureo-border-width-medium border-lureo-brand-ink text-lureo-brand-ink py-1 px-3 font-lureo-font-ui text-lureo-12 font-medium leading-[1.3]"
                      >
                        {DEVICE_LABELS[d]}
                      </span>
                    ))
                  )}
                </div>
              </Field>
            </div>
          </Card>

          {coverImageUrl ? (
            <div className="overflow-hidden rounded-lureo-radius-lg border-lureo-border-width border-lureo-line bg-lureo-neutral-subtle min-h-44">
              <img
                src={shownUrl(coverImageUrl)}
                alt={storyName || 'Story cover'}
                className="h-full w-full object-cover"
              />
            </div>
          ) : (
            <div className="rounded-lureo-radius-lg border-lureo-border-width border-lureo-line bg-lureo-neutral-subtle p-5 flex items-center justify-center">
              <p className="font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle leading-normal text-center">
                Once you submit, we'll review and let you know within 7 days via notification and email.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const submissionMessage = (
    <>
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-lureo-brand text-lureo-on-brand">
        <CheckCircleIcon className="h-8 w-8" />
      </div>
      <FlowTitle as="h3" className="mt-4">
        Story submitted
      </FlowTitle>
      <p className="mt-3 font-lureo-font-prose text-lureo-15 text-lureo-ink-subtle leading-normal">
        We'll review your story and let you know whether it was accepted or rejected within 7 days,
        via notification and email.
      </p>
    </>
  );

  if (!sceneInstanceId) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md px-6">{submissionMessage}</div>
      </div>
    );
  }

  return (
    <div className="h-full grid grid-cols-2 divide-x-lureo-border-width divide-lureo-line">
      <div className="flex flex-col items-center justify-center text-center pr-7">
        <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-lureo-brand text-lureo-on-brand">
          <CheckCircleIcon className="h-8 w-8" />
        </div>
        <FlowTitle as="h3" className="mt-4">Story submitted</FlowTitle>
        <p className="mt-2 font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle leading-[1.4]">
          We'll review and let you know within 7 days via notification and email.
        </p>
      </div>

      <div className="flex flex-col items-center justify-center pl-7">
        <SharePanel
          sceneInstanceId={sceneInstanceId}
          partialBlocks={partialBlocks}
          anchorCategory={anchorCategory}
        />
      </div>
    </div>
  );
}
